import React, { useState, useEffect, useRef } from 'react';
import SignatureField from './SignatureField';
import MapField from './MapField';
import ErrorBoundary from './ErrorBoundary';
import { mapFieldHasPlacedMarker } from '../../utils/mapFieldValidation';
import { formatFormDisplayName } from '../../utils/formName';
import './FormRenderer.css';

// Number input that blocks scroll-to-change (browsers use passive:true for wheel by default, so we need addEventListener)
const NumberInput = (props) => {
  const inputRef = useRef(null);
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const handler = (e) => {
      if (document.activeElement === el) {
        e.preventDefault();
      }
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);
  return <input ref={inputRef} type="number" {...props} />;
};

// Light background colors for different forms
const getFormBackgroundColor = (formInstanceId) => {
  const colors = [
    '#f0f9ff', // Light blue
    '#fef3c7', // Light yellow
    '#f0fdf4', // Light green
    '#fef2f2', // Light red
    '#f3e8ff', // Light purple
    '#fdf4ff', // Light pink
    '#ecfeff', // Light cyan
    '#f9fafb'  // Light gray
  ];

  // Create a simple hash of the form instance ID to get consistent colors
  let hash = 0;
  for (let i = 0; i < formInstanceId.length; i++) {
    hash = formInstanceId.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

const RepeatableGroupField = ({ field, value, onChange, readOnly, errors, onAddFromList }) => {
  // In read-only mode, use the value directly to ensure we display the actual data
  // In edit mode, use state management for interactivity
  const [items, setItems] = useState(Array.isArray(value) ? value : (field.min_items > 0 ? [{}] : []));

  // Determine appropriate button text based on field label
  const getAddButtonText = (label) => {
    if (!label) return 'Add Item';
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes('sibling')) return 'Add Sibling';
    if (lowerLabel.includes('school')) return 'Add School';
    if (lowerLabel.includes('student')) return 'Add Student';
    if (lowerLabel.includes('authorized') || lowerLabel.includes('pickup')) return 'Add Person';
    if (lowerLabel.includes('emergency contact')) return 'Add Contact';
    return 'Add Item';
  };

  useEffect(() => {
    // Always update items when value changes, even in read-only mode
    if (Array.isArray(value)) {
      setItems(value);
    } else if (field.min_items > 0 && items.length === 0) {
      setItems([{}]);
    }
  }, [value, field.min_items]);

  const addItem = () => {
    if (readOnly) return;
    const newItems = [...items, {}];
    setItems(newItems);
    onChange(newItems);
  };

  const removeItem = (index) => {
    if (readOnly) return;
    if (items.length <= (field.min_items || 0)) return; // Don't allow removing below minimum

    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    onChange(newItems);
  };

  const updateItem = (index, fieldName, fieldValue) => {
    const newItems = [...items];
    if (!newItems[index]) {
      newItems[index] = {};
    }
    newItems[index][fieldName] = fieldValue;
    setItems(newItems);
    onChange(newItems);
  };

  const renderItemField = (itemField, itemIndex, fieldIndex) => {
    const itemFieldName = itemField.name;
    const itemFieldValue = items[itemIndex]?.[itemFieldName] || (itemField.type === 'checkbox' ? [] : '');
    const itemFieldErrors = errors?.[`${field.name}.${itemIndex}.${itemFieldName}`] || {};

    // Simple field rendering for repeatable group items
    switch (itemField.type) {
      case 'text':
        return (
          <input
            type="text"
            value={itemFieldValue}
            onChange={(e) => !readOnly && updateItem(itemIndex, itemFieldName, e.target.value)}
            placeholder={itemField.placeholder || ''}
            maxLength={itemField.max_length}
            readOnly={readOnly}
            className={`form-input ${readOnly ? 'read-only' : ''}`}
          />
        );

      case 'select':
        return (
          <select
            value={itemFieldValue}
            onChange={(e) => !readOnly && updateItem(itemIndex, itemFieldName, e.target.value)}
            disabled={readOnly}
            className={`form-select ${readOnly ? 'read-only' : ''}`}
          >
            <option value="">Select...</option>
            {itemField.options?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'checkbox':
        if (itemField.options && itemField.options.length > 1) {
          // Multi-select checkboxes
          return (
            <div className="checkbox-group">
              {itemField.options.map(option => (
                <label key={option} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={Array.isArray(itemFieldValue) && itemFieldValue.includes(option)}
                    onChange={(e) => {
                      const currentValues = Array.isArray(itemFieldValue) ? itemFieldValue : [];
                      const newValues = e.target.checked
                        ? [...currentValues, option]
                        : currentValues.filter(v => v !== option);
                      updateItem(itemIndex, itemFieldName, newValues);
                    }}
                    readOnly={readOnly}
                  />
                  {option}
                </label>
              ))}
            </div>
          );
        } else {
          // Single checkbox
          const option = itemField.options?.[0] || itemField.label;
          return (
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={itemFieldValue === true || itemFieldValue === option}
                onChange={(e) => updateItem(itemIndex, itemFieldName, e.target.checked ? option : false)}
                readOnly={readOnly}
              />
              {option}
            </label>
          );
        }

      case 'date':
        return (
          <input
            type="date"
            value={itemFieldValue || ''}
            onChange={(e) => !readOnly && updateItem(itemIndex, itemFieldName, e.target.value)}
            readOnly={readOnly}
            className={`form-input ${readOnly ? 'read-only' : ''}`}
          />
        );

      case 'number':
        return (
          <NumberInput
            value={itemFieldValue || ''}
            onChange={(e) => !readOnly && updateItem(itemIndex, itemFieldName, e.target.value)}
            readOnly={readOnly}
            className={`form-input ${readOnly ? 'read-only' : ''}`}
          />
        );

      default:
        return (
          <input
            type="text"
            value={itemFieldValue}
            onChange={(e) => updateItem(itemIndex, itemFieldName, e.target.value)}
            placeholder={itemField.placeholder || ''}
            readOnly={readOnly}
            className="form-input"
          />
        );
    }
  };

  return (
    <div className="repeatable-group-container">
      {items.length === 0 ? (
        <div className="repeatable-group-item">
          <div className="repeatable-group-header">
            <h4>No entries</h4>
          </div>
        </div>
      ) : (
        items.map((item, itemIndex) => (
          <div key={itemIndex} className="repeatable-group-item">
            <div className="repeatable-group-header">
              <h4>Entry {itemIndex + 1}</h4>
              {!readOnly && items.length > (field.min_items || 0) && (
                <button
                  type="button"
                  onClick={() => removeItem(itemIndex)}
                  className="btn-remove-item"
                >
                  Remove
                </button>
              )}
            </div>

            <div className="repeatable-group-fields">
              {field.fields?.map((itemField, fieldIndex) => (
                <div key={fieldIndex} className="form-field">
                  <label className="field-label">
                    {itemField.label}
                    {itemField.required && <span className="required-star">*</span>}
                  </label>
                  {renderItemField(itemField, itemIndex, fieldIndex)}
                  {itemField.note && (
                    <div className="field-note">{itemField.note}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {!readOnly && (
        <>
          <button
            type="button"
            onClick={addItem}
            className="btn-add-item"
          >
            {getAddButtonText(field.label)}
          </button>
          {onAddFromList && (
            <button
              type="button"
              onClick={onAddFromList}
              className="btn-add-from-list"
            >
              Add From Student List
            </button>
          )}
        </>
      )}
    </div>
  );
};

const FormRenderer = ({ formData, formValues, onChange, errors = {}, readOnly = false, autofillEnabled = {}, onAutofillToggle, userRole = 'parent', isFieldReadOnly, onAddSiblingsFromList, onFillFromSubmission, shouldShowSectionFillButton, fieldAmendments = {}, hideFormYear = false }) => {
  const [localValues, setLocalValues] = useState(formValues || {});
  const [focusedPhoneField, setFocusedPhoneField] = useState(null);

  useEffect(() => {
    if (formValues) {
      setLocalValues(prev => {
        // Never overwrite user-entered values with empty - merge to preserve (fixes helper form fields clearing)
        const hasValue = (v) => v !== null && v !== undefined && v !== '' &&
          v !== 'Please select an option' &&
          !(Array.isArray(v) && v.length === 0);
        const merged = { ...formValues };
        let changed = false;
        Object.keys(prev).forEach(k => {
          if (hasValue(prev[k]) && (!(k in formValues) || !hasValue(formValues[k]))) {
            merged[k] = prev[k];
            changed = true;
          }
        });
        return changed ? merged : formValues;
      });
    }
  }, [formValues]);

  // Format phone for display only (optional formatting, doesn't block backspace)
  // Store raw value - validation shows "Please enter a valid phone number" when invalid
  const formatPhoneForDisplay = (value) => {
    if (!value || typeof value !== 'string') return '';
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
    if (digits.length >= 3) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}`;
    }
    return digits;
  };

  const formatSSN = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Apply SSN format: 111-11-1111
    if (digits.length >= 5) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
    } else if (digits.length >= 3) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}`;
    } else {
      return digits;
    }
  };

  const handleFieldChange = (fieldName, value, fieldType, field = null) => {
    let processedValue = value;

    // Phone: allow free editing (no formatting during typing - fixes backspace issue)
    // Only strip non-digits for storage; validation shows warning when invalid
    if (fieldType === 'phone') {
      processedValue = (value || '').replace(/\D/g, '').slice(0, 10);
    }

    // Apply formatting for SSN fields (but not last_four which should be plain 4 digits)
    if (fieldType === 'ssn' ||
        (fieldName && (
          fieldName.includes('social_security_number') ||
          (fieldName.includes('ssn') && !fieldName.includes('ssn_last_four'))
        ))
    ) {
      processedValue = formatSSN(value);
    }

    // Handle checkboxes - distinguish between single and multi-select
    if (fieldType === 'checkbox') {
      // If field is provided and has options, it's multi-select
      if (field && field.options) {
        const currentValues = localValues[fieldName] || [];
        if (Array.isArray(currentValues)) {
          if (currentValues.includes(value)) {
            processedValue = currentValues.filter(v => v !== value);
          } else {
            processedValue = [...currentValues, value];
          }
        } else {
          processedValue = [value];
        }
      }
      // For single checkboxes without options, value is already boolean
    }

    setLocalValues((prevValues) => {
      const newValues = {
        ...prevValues,
        [fieldName]: processedValue
      };
      if (onChange) {
        onChange(newValues);
      }
      return newValues;
    });
  };

  const isFieldVisible = (field) => {
    // Check if field is admin-only and user is not admin
    if (field.admin_only && userRole !== 'district_admin' && userRole !== 'district_editor' && userRole !== 'superadmin') {
      return false;
    }

    // Hide individual authorized person fields when there's a repeating group
    // This prevents showing outdated individual fields alongside the repeating group
    if (field.name && (
      field.name.includes('authorized_person_first_name') ||
      field.name.includes('authorized_person_last_name')
    )) {
      // Check if there's a repeating group field in the FORM DEFINITION
      const hasRepeatingGroupInDefinition = formData?.sections?.some(section =>
        section.fields?.some(f =>
          f.type === 'repeatable_group' && f.name === 'repeating_group_4'
        )
      );

      // Also check if the SUBMITTED DATA contains repeating group data
      // This handles cases where the form definition is outdated but submissions have the data
      const hasRepeatingGroupInData = localValues?.repeating_group_4 &&
                                     Array.isArray(localValues.repeating_group_4) &&
                                     localValues.repeating_group_4.length > 0;

      if (hasRepeatingGroupInDefinition || hasRepeatingGroupInData) {
        return false; // Hide individual fields when repeating group exists
      }
    }

    if (!field.conditional) return true;

    const conditionalField = field.conditional.field;
    const conditionalValue = field.conditional.value;
    const operator = field.conditional.operator;
    const currentValue = localValues[conditionalField];

    if (operator === 'not_empty') {
      // Show field when the conditional field has a non-empty, non-null, non-zero value
      return currentValue !== '' && currentValue !== null && currentValue !== 0 && currentValue !== undefined;
    }

    // Default behavior: show when current value is in the conditional values array
    return conditionalValue.includes(currentValue);
  };

  const isFieldRequired = (field) => {
    if (field.required) return true;
    if (field.conditional_required && isFieldVisible(field)) {
      return true;
    }
    return false;
  };

  const renderSectionNote = (sectionNote) => {
    if (!sectionNote) return null;

    // Split by double newlines first to get paragraphs
    const paragraphs = sectionNote.split(/\n\n+/);

    return paragraphs.map((paragraph, index) => {
      const trimmed = paragraph.trim();
      if (!trimmed) return null;

      // Check if this paragraph should be styled as a note
      const isNote = trimmed.startsWith('Note:') ||
                     /^\*\*[^*]+\*\*$/.test(trimmed) ||
                     (trimmed.includes('**') && trimmed.startsWith('**'));

      if (isNote) {
        return (
          <div key={index} className="form-note">
            <p>{trimmed.replace(/\*\*/g, '')}</p>
          </div>
        );
      } else {
        // Regular section text
        return (
          <p key={index} className="section-note">
            {trimmed}
          </p>
        );
      }
    }).filter(Boolean);
  };

  const renderField = (field, sectionIndex, fieldIndex, formInstanceId) => {
    if (!isFieldVisible(field)) return null;

    const fieldName = field.name;
    const fieldValue = localValues[fieldName] || (field.type === 'checkbox' ? (field.options ? [] : false) : '');
    const amendmentHistory = fieldAmendments?.[fieldName];
    const latestAmendment = Array.isArray(amendmentHistory)
      ? amendmentHistory[amendmentHistory.length - 1]
      : amendmentHistory;
    const hasAmendment = readOnly && !!latestAmendment;
    const isRequired = isFieldRequired(field);
    const hasError = errors[fieldName];
    // Multi-select checkboxes use []; [] is truthy — still "empty" for required validation.
    const isEmptyRequired =
      isRequired &&
      (field.type === 'checkbox'
        ? field.options && field.options.length > 0
          ? !Array.isArray(fieldValue) || fieldValue.length === 0
          : !fieldValue
        : field.type === 'map'
          ? !mapFieldHasPlacedMarker(fieldValue)
          : !fieldValue || fieldValue === '');


    if (field.type === 'note') {
      return (
        <div key={`${sectionIndex}-${fieldIndex}`} className="form-note">
          <p>{field.label}</p>
        </div>
      );
    }

    if (field.type === 'repeatable_group') {
      return (
        <div key={`${sectionIndex}-${fieldIndex}`} className={`form-field repeatable-group ${hasError ? 'has-error' : ''} ${isEmptyRequired ? 'required-empty' : ''}`}>
          <label className="field-label">
            {field.label}
            {isRequired && <span className="required-star">*</span>}
          </label>
          <RepeatableGroupField
            field={field}
            value={fieldValue}
            onChange={(value) => handleFieldChange(fieldName, value, field.type)}
            readOnly={readOnly}
            errors={errors}
            onAddFromList={
              (field?.label || '').toLowerCase().includes('sibling') ? onAddSiblingsFromList : null
            }
          />
        </div>
      );
    }

    // Check if this is a signature field (by name or type)
    const isSignatureField = field.type === 'signature' ||
                             (field.name && field.name.toLowerCase().includes('signature') && field.type === 'text');

    // Check if this field should be read-only
    const fieldReadOnly = readOnly || field.readOnly || (isFieldReadOnly && isFieldReadOnly(field.name, fieldName));


    return (
      <div key={`${sectionIndex}-${fieldIndex}`} id={`field-${fieldName}`} className={`form-field ${hasError ? 'has-error' : ''} ${isEmptyRequired ? 'required-empty' : ''}`}>
        <label className="field-label">
          {field.label}
          {isRequired && <span className="required-star">*</span>}
          {hasAmendment && <span className="amendment-star" title="Value was amended after submission">*</span>}
          {fieldReadOnly && <span className="read-only-indicator">Locked</span>}
        </label>
        
        {isSignatureField ? (
          fieldReadOnly ? (
            (() => {
              let imageData = null;
              let metadata = null;
              
              // Handle both old format (just image string) and new format (object with metadata)
              if (fieldValue) {
                if (typeof fieldValue === 'string') {
                  if (fieldValue.startsWith('{')) {
                    try {
                      const signatureData = JSON.parse(fieldValue);
                      imageData = signatureData.image;
                      metadata = signatureData.metadata;
                    } catch (e) {
                      // If parsing fails, assume it's just the image
                      imageData = fieldValue;
                    }
                  } else if (fieldValue.startsWith('data:image')) {
                    imageData = fieldValue;
                  }
                } else if (typeof fieldValue === 'object' && fieldValue.image) {
                  // New format - object with image and metadata
                  imageData = fieldValue.image;
                  metadata = fieldValue.metadata;
                }
              }
              
              if (imageData && imageData.startsWith('data:image')) {
                return (
                  <div className="signature-display">
                    <img src={imageData} alt="Signature" />
                    {metadata && (
                      <div className="signature-metadata" style={{ 
                        marginTop: '0.5rem', 
                        fontSize: '0.75rem', 
                        color: '#666',
                        padding: '0.5rem',
                        background: '#f5f5f5',
                        borderRadius: '4px'
                      }}>
                        <details>
                          <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Signature Details</summary>
                          <div style={{ marginTop: '0.5rem', lineHeight: '1.6' }}>
                            <div><strong>Device:</strong> {metadata.deviceType || 'Unknown'}</div>
                            {metadata.ipAddress && <div><strong>IP Address:</strong> {metadata.ipAddress}</div>}
                            {metadata.location && (
                              <div>
                                <strong>Location:</strong> {metadata.location.latitude.toFixed(4)}, {metadata.location.longitude.toFixed(4)}
                                {metadata.location.accuracy && ` (±${Math.round(metadata.location.accuracy)}m)`}
                              </div>
                            )}
                            {metadata.capturedAt && (
                              <div><strong>Captured:</strong> {new Date(metadata.capturedAt).toLocaleString()}</div>
                            )}
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                );
              } else {
                return (
                  <div className="signature-display" style={{ padding: '1rem', color: '#999', fontStyle: 'italic' }}>
                    No signature provided
                  </div>
                );
              }
            })()
          ) : (
            <SignatureField
              value={fieldValue}
              onChange={(value) => handleFieldChange(fieldName, value, 'signature')}
              required={isRequired}
              label={field.label}
              name={fieldName}
            />
          )
        ) : field.type === 'text' || field.type === 'email' || field.type === 'phone' || field.type === 'ssn' ? (
          <input
            type={field.type === 'email' ? 'email' : field.type === 'phone' || field.type === 'ssn' ? 'tel' : 'text'}
            name={fieldName}
            value={field.type === 'phone'
              ? (focusedPhoneField === fieldName ? (fieldValue || '') : formatPhoneForDisplay(fieldValue))
              : fieldValue}
            onChange={(e) => handleFieldChange(fieldName, e.target.value, field.type)}
            onFocus={field.type === 'phone' ? () => setFocusedPhoneField(fieldName) : undefined}
            onBlur={field.type === 'phone' ? () => setFocusedPhoneField(null) : undefined}
            required={isRequired}
            placeholder={field.type === 'phone' ? (field.placeholder || '555-555-5555') : (field.placeholder || '')}
            pattern={field.pattern || undefined}
            readOnly={fieldReadOnly}
            className="field-input"
          />
        ) : field.type === 'number' ? (
          <NumberInput
            name={fieldName}
            value={fieldValue}
            onChange={(e) => handleFieldChange(fieldName, e.target.value, field.type)}
            required={isRequired}
            readOnly={fieldReadOnly}
            className="field-input"
          />
        ) : field.type === 'date' ? (
          <input
            type="date"
            name={fieldName}
            value={fieldValue}
            onChange={(e) => handleFieldChange(fieldName, e.target.value, field.type)}
            required={isRequired}
            readOnly={fieldReadOnly}
            className="field-input"
          />
        ) : field.type === 'textarea' ? (
          <textarea
            name={fieldName}
            value={fieldValue}
            onChange={(e) => handleFieldChange(fieldName, e.target.value, field.type)}
            required={isRequired}
            placeholder={field.placeholder || ''}
            rows={4}
            readOnly={fieldReadOnly}
            className="field-textarea"
          />
        ) : field.type === 'select' ? (
          <select
            name={fieldName}
            value={fieldValue}
            onChange={(e) => handleFieldChange(fieldName, e.target.value, field.type)}
            required={isRequired}
            disabled={fieldReadOnly}
            className="field-select"
          >
            <option value="">{field.placeholder || 'Please select an option'}</option>
            {field.options && field.options.map((option, optIndex) => (
              <option key={optIndex} value={option}>{option}</option>
            ))}
          </select>
        ) : field.type === 'radio' ? (
          <div className="field-radio-group">
            {field.options && field.options.map((option, optIndex) => (
              <label key={optIndex} className="radio-label">
                <input
                  type="radio"
                  name={fieldName}
                  value={option}
                  checked={fieldValue === option}
                  onChange={(e) => handleFieldChange(fieldName, e.target.value, field.type)}
                  required={isRequired}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        ) : field.type === 'checkbox' ? (
          field.options ? (
            // Multi-select checkboxes
            <div className="field-checkbox-group">
              {field.options.map((option, optIndex) => (
                <label key={optIndex} className="checkbox-label">
                  <input
                    type="checkbox"
                    name={`${fieldName}_${optIndex}`}
                    value={option}
                    checked={Array.isArray(fieldValue) && fieldValue.includes(option)}
                    onChange={(e) => handleFieldChange(fieldName, option, field.type, field)}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          ) : (
            // Single checkbox
            <label className="checkbox-label">
              <input
                type="checkbox"
                name={fieldName}
                checked={Boolean(fieldValue)}
                onChange={(e) => handleFieldChange(fieldName, e.target.checked, field.type, field)}
              />
              <span>{field.label}</span>
            </label>
          )
        ) : field.type === 'map' ? (
          /*
           * The map is the one field here that depends on a third-party library, remote
           * tiles and a geocoding service, so it is the most likely thing on the page to
           * throw. Without this boundary a Leaflet exception reached the app-level one and
           * took the WHOLE enrollment form down with it - losing everything typed since
           * the last save. Now a map failure costs the map and nothing else.
           */
          <ErrorBoundary
            fallback={(
              <div className="map-field-fallback">
                <p className="map-field-fallback-title">The map could not be loaded.</p>
                <p className="map-field-fallback-body">
                  Nothing you have entered has been lost, and <strong>you can still finish and
                  submit this form</strong> - the map is not required. Everything else here
                  still works and your answers are saved as you go. Reloading the page will
                  usually bring the map back if you would like to pin your home, but the school
                  can confirm your address with you instead.
                </p>
              </div>
            )}
          >
            <MapField
              field={field}
              value={fieldValue}
              onChange={(coordinates) => handleFieldChange(fieldName, coordinates, field.type)}
              readOnly={readOnly}
              errors={errors}
              allFormValues={localValues}
              formInstanceId={formInstanceId}
            />
          </ErrorBoundary>
        ) : null}

        {hasError && <span className="error-message">{hasError}</span>}
        {hasAmendment && (
          <span className="field-amendment-note">
            Previous value: {latestAmendment.previous_value === '' ? '(blank)' : String(latestAmendment.previous_value ?? '(blank)')}
          </span>
        )}
      </div>
    );
  };

  if (!formData || !formData.sections) {
    return <div>No form data available</div>;
  }

  // Check for repeating group data that exists in form values but not in form definition
  // This handles cases where form definition is outdated but submissions contain new field types
  const processedFormData = { ...formData };
  const hasRepeatingGroupData = localValues?.repeating_group_4 &&
                               Array.isArray(localValues.repeating_group_4) &&
                               localValues.repeating_group_4.length > 0;

  const hasRepeatingGroupInDefinition = formData.sections?.some(section =>
    section.fields?.some(field =>
      field.type === 'repeatable_group' && field.name === 'repeating_group_4'
    )
  );

  // If we have repeating group data but no definition, inject a section for it
  if (hasRepeatingGroupData && !hasRepeatingGroupInDefinition) {

    const repeatingGroupSection = {
      section_name: 'AUTHORIZED PERSONS FOR EARLY DISMISSAL',
      fields: [{
        name: 'repeating_group_4',
        label: 'Authorized Persons for Early Dismissal',
        type: 'repeatable_group',
        fields: [
          { name: 'authorized_person_first_name', label: 'First Name', type: 'text' },
          { name: 'authorized_person_last_name', label: 'Last Name', type: 'text' }
        ]
      }]
    };

    processedFormData.sections = [...(processedFormData.sections || []), repeatingGroupSection];
  }

  return (
    <div className="form-renderer">
      {processedFormData.sections.map((section, sectionIndex) => {
        // Skip admin-only sections for parent view
        if (section.admin_only) return null;

        // Check section-level conditionals
        if (section.conditional) {
          const conditionalField = section.conditional.field;
          const conditionalValue = section.conditional.value;
          let currentValue = localValues[conditionalField];

          // If the conditional field is not found with exact name, try to find it across forms
          // by looking for any field that ends with the conditional field name (cross-form reference)
          if (currentValue === undefined) {
            for (const [fieldKey, fieldValue] of Object.entries(localValues)) {
              if (fieldKey.endsWith('_' + conditionalField)) {
                currentValue = fieldValue;
                break;
              }
            }
          }

          // If conditional value is a string, convert to array for includes check
          const valuesToCheck = Array.isArray(conditionalValue) ? conditionalValue : [conditionalValue];

          // Skip section if conditional is not met
          if (!valuesToCheck.includes(currentValue)) {
            return null;
          }
        }

        // Handle form header sections (form titles)
        if (section.is_form_header) {
          const backgroundColor = section.form_instance_id ? getFormBackgroundColor(section.form_instance_id) : 'transparent';
          const instanceId = section.form_instance_id;
          const hasAutofillCheckbox = instanceId && autofillEnabled.hasOwnProperty(instanceId);

          return (
            <div key={sectionIndex} className="form-header-section" style={{ backgroundColor }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 className="form-title">
                  {hideFormYear ? formatFormDisplayName(section.section_name, { includeYear: false }) : section.section_name}
                  {section.is_required && <span style={{ color: '#dc3545', marginLeft: '0.5rem', fontSize: '0.9em' }}>⚠️ Required</span>}
                </h2>
                {hasAutofillCheckbox && (
                  <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem', fontWeight: 'normal' }}>
                    <input
                      type="checkbox"
                      checked={autofillEnabled[instanceId] !== false}
                      onChange={(e) => onAutofillToggle && onAutofillToggle(instanceId, e.target.checked)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    Autofilled
                  </label>
                )}
                  {shouldShowSectionFillButton && shouldShowSectionFillButton(section) && section.form_instance_id && onFillFromSubmission && (
                    <button
                      type="button"
                      className="section-fill-button"
                      onClick={() => onFillFromSubmission(section.form_instance_id)}
                    >
                      Fill from Submissions
                    </button>
                  )}
              </div>
              {section.section_note && renderSectionNote(section.section_note)}
            </div>
          );
        }

        // Check if any fields in this section are visible
        const hasVisibleFields = section.fields.some(field => isFieldVisible(field));

        // Skip rendering section if no fields are visible
        if (!hasVisibleFields) {
          return null;
        }

        const backgroundColor = section.form_instance_id ? getFormBackgroundColor(section.form_instance_id) : 'transparent';
        const baseSectionId = section.section_name ? section.section_name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : `section-${sectionIndex}`;
        // Make section ID unique by including form instance ID to avoid conflicts between forms
        const sectionId = section.form_instance_id ? `${baseSectionId}-${section.form_instance_id}` : baseSectionId;
        return (
          <div key={sectionIndex} id={sectionId} className="form-section" style={{ backgroundColor }}>
            <h3 className="section-title">{section.section_name}</h3>
            {section.section_note && renderSectionNote(section.section_note)}
            <div className="section-fields">
              {section.fields.map((field, fieldIndex) => renderField(field, sectionIndex, fieldIndex, section.form_instance_id))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FormRenderer;


